import { approximateJsonBytes, assertBlobIntegrity, createBlobRef } from './bytes.js';
import { StorageError, mapStorageError } from './errors.js';
import { pendingMigrations } from './migrations.js';
import { checkExpectedVersion, createDeletedDocument, createPutDocument, defaultClock, defaultIdSource, documentKey, operationInputForDocument, } from './model.js';
import { assertBlobRef, assertJsonValue, assertOutboxOperation, assertStoredDocument, normalizeQueryWindow, } from './validation.js';
const DATABASE_VERSION = 1;
const DOCUMENT_STORE = 'documents';
const OUTBOX_STORE = 'outbox';
const METADATA_STORE = 'metadata';
const BLOB_STORE = 'blobs';
const SCHEMA_VERSION_KEY = 'schema-version';
function requestResult(request) {
    return new Promise((resolve, reject) => {
        request.addEventListener('success', () => resolve(request.result), { once: true });
        request.addEventListener('error', () => reject(request.error ?? new StorageError('transaction-failed')), {
            once: true,
        });
    });
}
function transactionCompletion(transaction) {
    return new Promise((resolve, reject) => {
        transaction.addEventListener('complete', () => resolve(), { once: true });
        transaction.addEventListener('abort', () => reject(transaction.error ?? new StorageError('transaction-failed')), {
            once: true,
        });
        transaction.addEventListener('error', () => reject(transaction.error ?? new StorageError('transaction-failed')), {
            once: true,
        });
    });
}
function keepTransactionAlive(transaction, storeName) {
    let active = true;
    const store = transaction.objectStore(storeName);
    const pulse = () => {
        if (!active)
            return;
        let request;
        try {
            request = store.get('__tchoma_transaction_keepalive__');
        }
        catch {
            return;
        }
        const continuePulse = () => {
            if (active)
                pulse();
        };
        request.addEventListener('success', continuePulse, { once: true });
        request.addEventListener('error', continuePulse, { once: true });
    };
    pulse();
    return () => {
        active = false;
    };
}
async function runTransaction(database, stores, mode, run) {
    const transaction = database.transaction(stores, mode);
    const completion = transactionCompletion(transaction);
    try {
        const value = await run(transaction);
        await completion;
        return value;
    }
    catch (error) {
        try {
            transaction.abort();
        }
        catch {
            // The transaction may already be aborted by IndexedDB.
        }
        try {
            await completion;
        }
        catch {
            // Preserve the original, more specific failure.
        }
        throw mapStorageError(error);
    }
}
async function openDatabase(factory, name) {
    if (name.length === 0 || name.length > 256) {
        throw new StorageError('invalid-data', { detail: 'IndexedDB databaseName is invalid' });
    }
    const request = factory.open(name, DATABASE_VERSION);
    return new Promise((resolve, reject) => {
        request.addEventListener('upgradeneeded', () => {
            const database = request.result;
            if (!database.objectStoreNames.contains(DOCUMENT_STORE)) {
                const documents = database.createObjectStore(DOCUMENT_STORE, { keyPath: 'key' });
                documents.createIndex('by-type', 'document.type', { unique: false });
            }
            if (!database.objectStoreNames.contains(OUTBOX_STORE)) {
                const outbox = database.createObjectStore(OUTBOX_STORE, { keyPath: 'sequence', autoIncrement: true });
                outbox.createIndex('by-op-id', 'opId', { unique: true });
            }
            if (!database.objectStoreNames.contains(METADATA_STORE)) {
                database.createObjectStore(METADATA_STORE, { keyPath: 'key' });
            }
            if (!database.objectStoreNames.contains(BLOB_STORE)) {
                database.createObjectStore(BLOB_STORE, { keyPath: 'id' });
            }
        });
        request.addEventListener('success', () => resolve(request.result), { once: true });
        request.addEventListener('error', () => reject(mapStorageError(request.error)), { once: true });
        request.addEventListener('blocked', () => reject(new StorageError('transaction-failed', {
            detail: `IndexedDB database ${name} is blocked by another open connection`,
        })), { once: true });
    });
}
function sameOperation(input, existing) {
    return input.kind === existing.kind
        && input.documentType === existing.documentType
        && input.documentId === existing.documentId
        && input.documentVersion === existing.documentVersion
        && JSON.stringify(input.payload) === JSON.stringify(existing.payload);
}
async function getOperationById(store, opId) {
    return requestResult(store.index('by-op-id').get(opId));
}
async function enqueueOperation(store, input, clock, idSource) {
    const opId = input.opId ?? idSource.create();
    const existing = await getOperationById(store, opId);
    if (existing) {
        if (!sameOperation(input, existing))
            throw new StorageError('integrity-failed');
        return structuredClone(existing);
    }
    const pending = {
        opId,
        kind: input.kind,
        documentType: input.documentType,
        documentId: input.documentId,
        documentVersion: input.documentVersion,
        payload: structuredClone(input.payload),
        createdAt: input.createdAt ?? clock.now(),
    };
    const sequence = Number(await requestResult(store.add(pending)));
    const operation = { ...pending, sequence };
    assertOutboxOperation(operation);
    return operation;
}
class IndexedDbDocumentTransaction {
    transaction;
    mode;
    clock;
    idSource;
    emitOutbox;
    active = true;
    constructor(transaction, mode, clock, idSource, emitOutbox) {
        this.transaction = transaction;
        this.mode = mode;
        this.clock = clock;
        this.idSource = idSource;
        this.emitOutbox = emitOutbox;
    }
    finish() {
        this.active = false;
    }
    assertActive() {
        if (!this.active)
            throw new StorageError('transaction-failed', { detail: 'Transaction is no longer active' });
    }
    documentStore() {
        this.assertActive();
        return this.transaction.objectStore(DOCUMENT_STORE);
    }
    outboxStore() {
        this.assertActive();
        return this.transaction.objectStore(OUTBOX_STORE);
    }
    async get(type, id, options) {
        const record = await requestResult(this.documentStore().get(documentKey(type, id)));
        const document = record?.document;
        if (!document || (document.deletedAt !== null && !options?.includeDeleted))
            return null;
        return structuredClone(document);
    }
    async put(input, options = {}) {
        const key = documentKey(input.type, input.id);
        assertJsonValue(input.data);
        if (options.opId && this.emitOutbox && this.mode === 'sync-ready') {
            const previousOperation = await getOperationById(this.outboxStore(), options.opId);
            if (previousOperation) {
                if (previousOperation.kind !== 'document-put'
                    || previousOperation.documentType !== input.type
                    || previousOperation.documentId !== input.id) {
                    throw new StorageError('integrity-failed');
                }
                if (JSON.stringify(previousOperation.payload.data) !== JSON.stringify(input.data)) {
                    throw new StorageError('integrity-failed');
                }
                return structuredClone(previousOperation.payload);
            }
        }
        const currentRecord = await requestResult(this.documentStore().get(key));
        checkExpectedVersion(currentRecord?.document, options.expectedVersion);
        const document = createPutDocument(input, currentRecord?.document, this.clock.now());
        await requestResult(this.documentStore().put({ key, document: structuredClone(document) }));
        if (this.emitOutbox && this.mode === 'sync-ready') {
            const opId = options.opId ?? this.idSource.create();
            await enqueueOperation(this.outboxStore(), operationInputForDocument(document, 'document-put', opId), this.clock, this.idSource);
        }
        return structuredClone(document);
    }
    async delete(type, id, options = {}) {
        const key = documentKey(type, id);
        if (options.opId && this.emitOutbox && this.mode === 'sync-ready') {
            const previousOperation = await getOperationById(this.outboxStore(), options.opId);
            if (previousOperation) {
                if (previousOperation.kind !== 'document-delete'
                    || previousOperation.documentType !== type
                    || previousOperation.documentId !== id) {
                    throw new StorageError('integrity-failed');
                }
                return structuredClone(previousOperation.payload);
            }
        }
        const currentRecord = await requestResult(this.documentStore().get(key));
        checkExpectedVersion(currentRecord?.document, options.expectedVersion);
        if (!currentRecord)
            return null;
        const document = createDeletedDocument(currentRecord.document, this.clock.now());
        await requestResult(this.documentStore().put({ key, document: structuredClone(document) }));
        if (this.emitOutbox && this.mode === 'sync-ready') {
            const opId = options.opId ?? this.idSource.create();
            await enqueueOperation(this.outboxStore(), operationInputForDocument(document, 'document-delete', opId), this.clock, this.idSource);
        }
        return structuredClone(document);
    }
    async list(query = {}) {
        const { limit, offset } = normalizeQueryWindow(query.limit, query.offset);
        const records = await requestResult(this.documentStore().getAll());
        return records
            .map(({ document }) => document)
            .filter((document) => (query.type === undefined || document.type === query.type)
            && (query.includeDeleted === true || document.deletedAt === null))
            .sort((left, right) => left.type.localeCompare(right.type) || left.id.localeCompare(right.id))
            .slice(offset, offset + limit)
            .map((document) => structuredClone(document));
    }
    query(query = {}) {
        return this.list(query);
    }
}
export class IndexedDbBlobStore {
    database;
    clock;
    backendKind = 'indexeddb';
    constructor(database, clock) {
        this.database = database;
        this.clock = clock;
    }
    async putPrepared(ref, input) {
        assertBlobRef(ref);
        await assertBlobIntegrity(input, ref);
        const bytes = await input.arrayBuffer();
        await runTransaction(this.database, [BLOB_STORE], 'readwrite', async (transaction) => {
            const record = {
                id: ref.id,
                ref: structuredClone(ref),
                bytes,
            };
            await requestResult(transaction.objectStore(BLOB_STORE).put(record));
        });
    }
    async putReference(ref) {
        assertBlobRef(ref);
        await runTransaction(this.database, [BLOB_STORE], 'readwrite', async (transaction) => {
            const record = { id: ref.id, ref: structuredClone(ref) };
            await requestResult(transaction.objectStore(BLOB_STORE).put(record));
        });
    }
    async getRecord(id) {
        return runTransaction(this.database, [BLOB_STORE], 'readonly', async (transaction) => requestResult(transaction.objectStore(BLOB_STORE).get(id)));
    }
    async put(input, options = {}) {
        const ref = await createBlobRef(input, options.mime ?? (input.type || 'application/octet-stream'), this.clock);
        await this.putPrepared(ref, input);
        return ref;
    }
    async get(ref) {
        assertBlobRef(ref);
        const record = await this.getRecord(ref.id);
        if (!record)
            return null;
        const blob = record.bytes instanceof ArrayBuffer
            ? new Blob([record.bytes], { type: record.ref.mime })
            : record.blob?.slice(0, record.blob.size, record.ref.mime);
        if (!blob)
            return null;
        await assertBlobIntegrity(blob, ref);
        return blob;
    }
    async delete(ref) {
        assertBlobRef(ref);
        await runTransaction(this.database, [BLOB_STORE], 'readwrite', async (transaction) => {
            await requestResult(transaction.objectStore(BLOB_STORE).delete(ref.id));
        });
    }
    async list(options = {}) {
        const { limit, offset } = normalizeQueryWindow(options.limit, options.offset);
        return runTransaction(this.database, [BLOB_STORE], 'readonly', async (transaction) => {
            const records = await requestResult(transaction.objectStore(BLOB_STORE).getAll());
            return records
                .map(({ ref }) => structuredClone(ref))
                .sort((left, right) => left.id.localeCompare(right.id))
                .slice(offset, offset + limit);
        });
    }
}
export class IndexedDbStorageBackend {
    mode;
    database;
    clock;
    idSource;
    documents;
    outbox;
    fallbackBlobs;
    closed = false;
    constructor(mode, database, clock, idSource) {
        this.mode = mode;
        this.database = database;
        this.clock = clock;
        this.idSource = idSource;
        this.documents = this.createDocumentStore();
        this.outbox = this.createOutboxStore();
        this.fallbackBlobs = new IndexedDbBlobStore(database, clock);
    }
    static async open(options) {
        const factory = options.indexedDB ?? globalThis.indexedDB;
        if (!factory)
            throw new StorageError('unsupported', { detail: 'IndexedDB is unavailable' });
        const database = await openDatabase(factory, options.databaseName);
        return new IndexedDbStorageBackend(options.mode ?? 'sync-ready', database, options.clock ?? defaultClock(), options.idSource ?? defaultIdSource());
    }
    assertOpen() {
        if (this.closed)
            throw new StorageError('closed');
    }
    createDocumentStore() {
        const transact = async (run) => {
            this.assertOpen();
            return runTransaction(this.database, [DOCUMENT_STORE, OUTBOX_STORE], 'readwrite', async (transaction) => {
                const release = keepTransactionAlive(transaction, DOCUMENT_STORE);
                const context = new IndexedDbDocumentTransaction(transaction, this.mode, this.clock, this.idSource, true);
                try {
                    return await run(context);
                }
                finally {
                    release();
                    context.finish();
                }
            });
        };
        const list = async (query) => {
            this.assertOpen();
            return runTransaction(this.database, [DOCUMENT_STORE], 'readonly', async (transaction) => {
                const context = new IndexedDbDocumentTransaction(transaction, this.mode, this.clock, this.idSource, false);
                try {
                    return await context.list(query);
                }
                finally {
                    context.finish();
                }
            });
        };
        return {
            get: async (type, id, options) => {
                this.assertOpen();
                return runTransaction(this.database, [DOCUMENT_STORE], 'readonly', async (transaction) => {
                    const context = new IndexedDbDocumentTransaction(transaction, this.mode, this.clock, this.idSource, false);
                    try {
                        return await context.get(type, id, options);
                    }
                    finally {
                        context.finish();
                    }
                });
            },
            put: async (input, options) => transact((transaction) => transaction.put(input, options)),
            delete: (type, id, options) => transact((transaction) => transaction.delete(type, id, options)),
            list,
            query: list,
            count: async (query = {}) => {
                this.assertOpen();
                return runTransaction(this.database, [DOCUMENT_STORE], 'readonly', async (transaction) => {
                    const records = await requestResult(transaction.objectStore(DOCUMENT_STORE).getAll());
                    return records.filter(({ document }) => (query.type === undefined || document.type === query.type)
                        && (query.includeDeleted === true || document.deletedAt === null)).length;
                });
            },
            transact,
        };
    }
    createOutboxStore() {
        return {
            enqueue: async (input) => {
                this.assertOpen();
                if (this.mode === 'local-only')
                    throw new StorageError('unsupported', {
                        detail: 'Outbox is disabled in local-only mode',
                    });
                return runTransaction(this.database, [OUTBOX_STORE], 'readwrite', (transaction) => enqueueOperation(transaction.objectStore(OUTBOX_STORE), input, this.clock, this.idSource));
            },
            list: async (query = {}) => {
                this.assertOpen();
                const { limit } = normalizeQueryWindow(query.limit, 0);
                const afterSequence = query.afterSequence ?? 0;
                if (!Number.isSafeInteger(afterSequence) || afterSequence < 0)
                    throw new StorageError('invalid-data');
                return runTransaction(this.database, [OUTBOX_STORE], 'readonly', async (transaction) => {
                    const operations = await requestResult(transaction.objectStore(OUTBOX_STORE).getAll());
                    return operations
                        .filter((operation) => operation.sequence > afterSequence)
                        .sort((left, right) => left.sequence - right.sequence)
                        .slice(0, limit)
                        .map((operation) => structuredClone(operation));
                });
            },
            acknowledge: async (opIds) => {
                this.assertOpen();
                return runTransaction(this.database, [OUTBOX_STORE], 'readwrite', async (transaction) => {
                    const store = transaction.objectStore(OUTBOX_STORE);
                    let deleted = 0;
                    for (const opId of new Set(opIds)) {
                        const operation = await getOperationById(store, opId);
                        if (!operation)
                            continue;
                        await requestResult(store.delete(operation.sequence));
                        deleted += 1;
                    }
                    return deleted;
                });
            },
            count: async () => {
                this.assertOpen();
                return runTransaction(this.database, [OUTBOX_STORE], 'readonly', async (transaction) => (requestResult(transaction.objectStore(OUTBOX_STORE).count())));
            },
        };
    }
    async getSchemaVersion() {
        this.assertOpen();
        return runTransaction(this.database, [METADATA_STORE], 'readonly', async (transaction) => {
            const record = await requestResult(transaction.objectStore(METADATA_STORE).get(SCHEMA_VERSION_KEY));
            return record?.value ?? 0;
        });
    }
    async migrate(migrations) {
        this.assertOpen();
        return runTransaction(this.database, [DOCUMENT_STORE, OUTBOX_STORE, METADATA_STORE], 'readwrite', async (transaction) => {
            const metadata = transaction.objectStore(METADATA_STORE);
            const record = await requestResult(metadata.get(SCHEMA_VERSION_KEY));
            let version = record?.value ?? 0;
            const pending = pendingMigrations(version, migrations);
            const release = keepTransactionAlive(transaction, DOCUMENT_STORE);
            const context = new IndexedDbDocumentTransaction(transaction, this.mode, this.clock, this.idSource, false);
            try {
                for (const migration of pending) {
                    try {
                        await migration.migrate(context);
                    }
                    catch (error) {
                        throw new StorageError('migration-failed', {
                            cause: error,
                            detail: `Migration ${migration.version} (${migration.name}) failed`,
                        });
                    }
                    version = migration.version;
                    await requestResult(metadata.put({ key: SCHEMA_VERSION_KEY, value: version }));
                }
                return version;
            }
            finally {
                release();
                context.finish();
            }
        });
    }
    async snapshot() {
        this.assertOpen();
        return runTransaction(this.database, [DOCUMENT_STORE, OUTBOX_STORE, METADATA_STORE], 'readonly', async (transaction) => {
            const [documentRecords, outbox, metadata] = await Promise.all([
                requestResult(transaction.objectStore(DOCUMENT_STORE).getAll()),
                requestResult(transaction.objectStore(OUTBOX_STORE).getAll()),
                requestResult(transaction.objectStore(METADATA_STORE).get(SCHEMA_VERSION_KEY)),
            ]);
            return {
                schemaVersion: metadata?.value ?? 0,
                documents: documentRecords
                    .map(({ document }) => structuredClone(document))
                    .sort((left, right) => left.type.localeCompare(right.type) || left.id.localeCompare(right.id)),
                outbox: outbox
                    .map((operation) => structuredClone(operation))
                    .sort((left, right) => left.sequence - right.sequence),
            };
        });
    }
    async replace(snapshot) {
        this.assertOpen();
        await runTransaction(this.database, [DOCUMENT_STORE, OUTBOX_STORE, METADATA_STORE], 'readwrite', async (transaction) => {
            const documents = transaction.objectStore(DOCUMENT_STORE);
            const outbox = transaction.objectStore(OUTBOX_STORE);
            const metadata = transaction.objectStore(METADATA_STORE);
            await Promise.all([
                requestResult(documents.clear()),
                requestResult(outbox.clear()),
                requestResult(metadata.put({
                    key: SCHEMA_VERSION_KEY,
                    value: snapshot.schemaVersion,
                })),
            ]);
            const writes = [];
            for (const document of snapshot.documents) {
                assertStoredDocument(document);
                writes.push(requestResult(documents.put({
                    key: documentKey(document.type, document.id),
                    document: structuredClone(document),
                })));
            }
            for (const operation of snapshot.outbox) {
                assertOutboxOperation(operation);
                writes.push(requestResult(outbox.put(structuredClone(operation))));
            }
            await Promise.all(writes);
        });
    }
    async merge(snapshot) {
        this.assertOpen();
        await runTransaction(this.database, [DOCUMENT_STORE, OUTBOX_STORE, METADATA_STORE], 'readwrite', async (transaction) => {
            const documents = transaction.objectStore(DOCUMENT_STORE);
            const outbox = transaction.objectStore(OUTBOX_STORE);
            const metadata = transaction.objectStore(METADATA_STORE);
            const metadataRecord = await requestResult(metadata.get(SCHEMA_VERSION_KEY));
            await requestResult(metadata.put({
                key: SCHEMA_VERSION_KEY,
                value: Math.max(metadataRecord?.value ?? 0, snapshot.schemaVersion),
            }));
            for (const incoming of snapshot.documents) {
                assertStoredDocument(incoming);
                const key = documentKey(incoming.type, incoming.id);
                const current = await requestResult(documents.get(key));
                if (!current || incoming.version > current.document.version) {
                    await requestResult(documents.put({ key, document: structuredClone(incoming) }));
                }
            }
            for (const incoming of snapshot.outbox) {
                assertOutboxOperation(incoming);
                const existing = await getOperationById(outbox, incoming.opId);
                if (existing)
                    continue;
                const { sequence: _ignored, ...pending } = structuredClone(incoming);
                await requestResult(outbox.add(pending));
            }
        });
    }
    async estimateUsageBytes() {
        this.assertOpen();
        return runTransaction(this.database, [DOCUMENT_STORE, OUTBOX_STORE, METADATA_STORE, BLOB_STORE], 'readonly', async (transaction) => {
            const [documentRecords, outbox, metadata, blobRecords] = await Promise.all([
                requestResult(transaction.objectStore(DOCUMENT_STORE).getAll()),
                requestResult(transaction.objectStore(OUTBOX_STORE).getAll()),
                requestResult(transaction.objectStore(METADATA_STORE).getAll()),
                requestResult(transaction.objectStore(BLOB_STORE).getAll()),
            ]);
            let usage = approximateJsonBytes({
                documentRecords,
                outbox,
                metadata,
                blobs: blobRecords.map(({ ref }) => ref),
            });
            for (const record of blobRecords)
                usage += record.bytes?.byteLength ?? record.blob?.size ?? 0;
            return usage;
        });
    }
    close() {
        if (this.closed)
            return;
        this.closed = true;
        this.database.close();
    }
}
//# sourceMappingURL=indexeddb.js.map